import { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from "@mui/material";
import {
  DEFAULT_PRESET_PV_KWP, DEFAULT_TILT, FACINGS, PRESET_ECONOMICS, PRESET_PRICES, PRESET_SHADING_PCT, PRESET_VNB, STUDY_PRESET_MODULE,
  facingLabel, studyKindOptions,
} from "./config/studyPresets";
import { getBatteries, getInverters, getModules } from "../services/api";
import { clearQuickStudyDefaults, loadQuickStudyDefaults, saveQuickStudyDefaults } from "./config/quickStudyDefaults";

// An optional positive figure: blank is allowed (filled in later), anything
// else must be a positive number. A decimal comma reads as a point.
const optionalPositive = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return { value: null, invalid: false };
  const n = Number(text.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? { value: n, invalid: false } : { value: null, invalid: true };
};
// A required figure: a price above 0 (positive) or a maintenance share of 0 or
// more. A decimal comma reads as a point.
const requiredNumber = (value, { allowZero = false } = {}) => {
  const text = String(value ?? "").trim();
  const n = Number(text.replace(",", "."));
  return text !== "" && Number.isFinite(n) && (allowZero ? n >= 0 : n > 0)
    ? { value: n, invalid: false } : { value: null, invalid: true };
};
// An optional figure of 0 or more: blank means none.
const optionalAtLeastZero = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return { value: null, invalid: false };
  const n = Number(text.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? { value: n, invalid: false } : { value: null, invalid: true };
};
// A percentage per year: 0 or more and below 100.
const requiredPercent = (value) => {
  const r = requiredNumber(value, { allowZero: true });
  return !r.invalid && r.value < 100 ? r : { value: null, invalid: true };
};
const itemName = (b) => String(b?.battery_name ?? b?.name ?? "").trim();
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);

/**
 * «Γρήγορη μελέτη» — opened by File → New. The user picks the kind of study
 * («Τι μελέτη θέλετε;») and the project starts from its settings
 * (config/studyPresets.js), with whatever customer figures and equipment are
 * given here. The project is saved when it is created.
 *
 * - «Δημιουργία έργου» creates and saves it, and the user goes on by hand.
 * - «Δημιουργία και εκτέλεση» creates it and runs the rest by itself once the
 *   location is set: auto-design, design save, study, results card. It needs
 *   the consumption and the price (and the battery, for PV with battery).
 * - «Παράλειψη» opens a new project with the platform's basic settings
 *   (DEFAULT_CONFIG), not saved, as File → New did before.
 * - Cancel and Esc leave the open project open and untouched.
 *
 * `onCreate(choice, { run })` may reject: the dialog then stays open and shows
 * why (a name already in use, the plan's project limit).
 *
 * The office's own starting values (consumer type, mounting, tilt, facing,
 * equipment, prices) can be kept for the next quick studies, in this browser
 * only (config/quickStudyDefaults.js, per `userId`).
 */
export default function NewStudyDialog({ open, isGreek, openProjectName, onCancel, onCreate, onSkip, userId = null }) {
  const tr = (el, en) => (isGreek ? el : en);
  const [kind, setKind] = useState("net_billing");
  const [useCase, setUseCase] = useState("Commercial");
  const [pvKwp, setPvKwp] = useState(String(DEFAULT_PRESET_PV_KWP));
  const [name, setName] = useState("");
  const [annualKwh, setAnnualKwh] = useState("");
  const [price, setPrice] = useState("");
  const [baseLoad, setBaseLoad] = useState("");
  // Virtual net billing: the supplier's energy price, the ΛΠ1-3 charge and
  // whether the surplus is paid.
  const [energyPrice, setEnergyPrice] = useState("");
  const [vnbCharge, setVnbCharge] = useState(PRESET_VNB.chargeEurPerMwh);
  const [surplusPaid, setSurplusPaid] = useState(PRESET_VNB.surplusPaid);
  const [mounting, setMounting] = useState("roof_frame");
  const [tilt, setTilt] = useState(String(DEFAULT_TILT));
  const [azimuth, setAzimuth] = useState(180);
  const [module, setModule] = useState(STUDY_PRESET_MODULE);
  const [inverter, setInverter] = useState(null);
  const [battery, setBattery] = useState(null);
  const [batteryOn, setBatteryOn] = useState(false);
  const [pvPrice, setPvPrice] = useState(PRESET_PRICES.pvEurPerKwp);
  const [pvOm, setPvOm] = useState(PRESET_PRICES.pvOmPctYear);
  const [batteryPrice, setBatteryPrice] = useState(PRESET_PRICES.batteryEurPerKwh);
  const [batteryOm, setBatteryOm] = useState(PRESET_PRICES.batteryOmPctYear);
  const [otherWorks, setOtherWorks] = useState(PRESET_ECONOMICS.otherWorksEur);
  const [insurance, setInsurance] = useState(PRESET_ECONOMICS.insurancePctYear);
  const [landLease, setLandLease] = useState(PRESET_ECONOMICS.landLeaseEurYear);
  const [inflation, setInflation] = useState(PRESET_ECONOMICS.inflationPctYear);
  const [nominal, setNominal] = useState(PRESET_ECONOMICS.nominalDiscountPctYear);
  const [catalog, setCatalog] = useState({ modules: null, inverters: null, batteries: null, error: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The office's saved starting values (this browser), and whether to keep the
  // dialog's values as the new ones.
  const [ownDefaults, setOwnDefaults] = useState(false);
  const [remember, setRemember] = useState(false);
  const applyStartingValues = (saved) => {
    const prices = saved?.prices || PRESET_PRICES;
    setUseCase(saved?.useCase || "Commercial");
    setMounting(saved?.mounting || "roof_frame");
    setTilt(String(saved?.tilt ?? DEFAULT_TILT));
    setAzimuth(saved?.azimuth ?? 180);
    setModule(saved?.module || STUDY_PRESET_MODULE);
    setInverter(saved?.inverter ? { name: saved.inverter } : null);
    setPvPrice(prices.pvEurPerKwp); setPvOm(prices.pvOmPctYear);
    setBatteryPrice(prices.batteryEurPerKwh); setBatteryOm(prices.batteryOmPctYear);
    const econ = saved?.economics || PRESET_ECONOMICS;
    setOtherWorks(econ.otherWorksEur); setInsurance(econ.insurancePctYear); setLandLease(econ.landLeaseEurYear);
    setInflation(econ.inflationPctYear); setNominal(econ.nominalDiscountPctYear);
  };
  useEffect(() => {
    if (!open) return;
    const saved = loadQuickStudyDefaults(userId);
    setKind("net_billing");
    setPvKwp(String(DEFAULT_PRESET_PV_KWP));
    setName(""); setAnnualKwh(""); setPrice(""); setBaseLoad("");
    setEnergyPrice(""); setVnbCharge(PRESET_VNB.chargeEurPerMwh); setSurplusPaid(PRESET_VNB.surplusPaid);
    setBattery(null); setBatteryOn(false);
    applyStartingValues(saved);
    setOwnDefaults(Boolean(saved)); setRemember(false);
    setBusy(false); setError("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const resetStartingValues = () => {
    clearQuickStudyDefaults(userId);
    applyStartingValues(null);
    setOwnDefaults(false); setRemember(false);
  };
  // The module and inverter catalogs, loaded once, when the dialog opens.
  useEffect(() => {
    if (!open || catalog.modules) return undefined;
    let alive = true;
    Promise.all([getModules(""), getInverters("")])
      .then(([modules, inverters]) => {
        if (alive) setCatalog((c) => ({ ...c, modules: asList(modules), inverters: asList(inverters) }));
      })
      .catch(() => {
        if (alive) setCatalog((c) => ({ ...c, modules: [], inverters: [], error: tr(
          "Δεν φορτώθηκε ο κατάλογος εξοπλισμού· χρησιμοποιούνται οι προεπιλογές. Αλλάζουν αργότερα, στη Ρύθμιση Μονάδας → ΦΒ Σύστημα.",
          "The equipment catalog could not be loaded; the defaults are used. They can be changed later, under Unit Setup → PV System.") }));
      });
    return () => { alive = false; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // The whole battery catalog, loaded the first time it is needed.
  useEffect(() => {
    if (!open || !batteryOn || catalog.batteries) return undefined;
    let alive = true;
    getBatteries({})
      .then((list) => { if (alive) setCatalog((c) => ({ ...c, batteries: asList(list) })); })
      .catch(() => { if (alive) setCatalog((c) => ({ ...c, batteries: [], batteryError: true })); });
    return () => { alive = false; };
  }, [open, batteryOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = studyKindOptions(isGreek);
  const kwp = Number(String(pvKwp).replace(",", "."));
  const kwpInvalid = !(Number.isFinite(kwp) && kwp > 0);
  const tiltNum = Number(String(tilt).replace(",", "."));
  const tiltInvalid = String(tilt).trim() === "" || !(Number.isFinite(tiltNum) && tiltNum >= 0 && tiltNum < 90);
  const kwh = optionalPositive(annualKwh);
  const eurKwh = optionalPositive(price);
  const withBattery = batteryOn;
  const pvEur = requiredNumber(pvPrice);
  const pvOmPct = requiredNumber(pvOm, { allowZero: true });
  const batteryEur = requiredNumber(batteryPrice);
  const batteryOmPct = requiredNumber(batteryOm, { allowZero: true });
  const otherWorksEur = optionalAtLeastZero(otherWorks);
  const insurancePct = requiredPercent(insurance);
  const landLeaseEur = optionalAtLeastZero(landLease);
  const inflationPct = requiredPercent(inflation);
  const nominalPct = requiredPercent(nominal);
  const pricesInvalid = pvEur.invalid || pvOmPct.invalid || (withBattery && (batteryEur.invalid || batteryOmPct.invalid))
    || otherWorksEur.invalid || insurancePct.invalid || landLeaseEur.invalid || inflationPct.invalid || nominalPct.invalid;
  const virtualNb = kind === "virtual_net_billing";
  const energyEurKwh = optionalPositive(energyPrice);
  const energyAbovePrice = virtualNb && energyEurKwh.value != null && eurKwh.value != null && energyEurKwh.value > eurKwh.value;
  const vnbChargeEur = requiredNumber(vnbCharge, { allowZero: true });
  const vnbInvalid = virtualNb && (energyEurKwh.invalid || energyAbovePrice || vnbChargeEur.invalid);
  // Base load (business only): kW that run every hour, opening hours or not.
  // Blank = none, as before. It cannot need more energy than the year's kWh.
  const business = useCase === "Commercial";
  const baseKw = optionalAtLeastZero(baseLoad);
  const baseMaxKw = kwh.value != null ? kwh.value / 8760 : null;
  const baseTooHigh = business && baseKw.value != null && baseKw.value > 0 && baseMaxKw != null && baseKw.value >= baseMaxKw;
  const baseInvalid = business && (baseKw.invalid || baseTooHigh);
  const baseSharePct = business && !baseInvalid && baseKw.value > 0 && kwh.value != null
    ? Math.round((baseKw.value * 8760 * 100) / kwh.value) : null;
  const invalid = kwpInvalid || tiltInvalid || kwh.invalid || eurKwh.invalid || pricesInvalid || vnbInvalid || baseInvalid;
  const figuresGiven = kwh.value != null && eurKwh.value != null;
  const batteryMissing = withBattery && !battery;
  const runMissing = [
    ...(kwh.value == null ? [tr("ετήσια κατανάλωση", "annual consumption")] : []),
    ...(eurKwh.value == null ? [tr("τιμή ρεύματος", "electricity price")] : []),
    ...(virtualNb && energyEurKwh.value == null ? [tr("τιμή ενέργειας προμηθευτή", "supplier's energy price")] : []),
    ...(batteryMissing ? [tr("μπαταρία", "battery")] : []),
  ];
  const steps = [
    tr("θέση στον χάρτη", "location on the map"),
    ...(figuresGiven ? [] : [tr("κατανάλωση και τιμή ρεύματος", "consumption and electricity price")]),
    ...(batteryMissing ? [tr("μπαταρία", "battery")] : []),
    tr("αυτόματος σχεδιασμός", "auto-design"),
    tr("αποθήκευση σχεδίου", "save the design"),
    tr("εκτέλεση", "run"),
  ];
  const flush = mounting === "roof_flush";
  const catalogLoading = open && !catalog.modules;

  const create = async (run) => {
    setBusy(true); setError("");
    const choice = {
      kind, useCase, pvKwp: kwp, name: name.trim(),
      annualKwh: kwh.value, priceEurKwh: eurKwh.value,
      ...(business && baseKw.value > 0 ? { baseLoadKw: baseKw.value } : {}),
      withBattery, battery: withBattery ? battery : null,
      ...(virtualNb ? { vnb: { energyPriceEurKwh: energyEurKwh.value, chargeEurPerMwh: vnbChargeEur.value, surplusPaid } } : {}),
      module: module || STUDY_PRESET_MODULE, inverter: inverter ? itemName(inverter) : "",
      mounting, tilt: tiltNum, azimuth,
      prices: {
        pvEurPerKwp: pvEur.value, pvOmPctYear: pvOmPct.value,
        ...(withBattery ? { batteryEurPerKwh: batteryEur.value, batteryOmPctYear: batteryOmPct.value } : {}),
      },
      economics: {
        otherWorksEur: otherWorksEur.value ?? "", insurancePctYear: insurancePct.value,
        landLeaseEurYear: landLeaseEur.value ?? "",
        inflationPctYear: inflationPct.value, nominalDiscountPctYear: nominalPct.value,
      },
    };
    if (remember) {
      // The battery prices are kept as they stand in the fields, even when this
      // study has no battery, so the next one with a battery starts from them.
      saveQuickStudyDefaults(userId, {
        ...choice,
        prices: { ...choice.prices, batteryEurPerKwh: batteryPrice, batteryOmPctYear: batteryOm },
      });
    }
    try {
      await onCreate(choice, { run });
    } catch (err) {
      setError(String(err?.message || err || tr("Το έργο δεν δημιουργήθηκε.", "The project was not created.")));
    } finally {
      setBusy(false);
    }
  };

  const section = (title) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>{title}</Typography>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth aria-labelledby="new-study-title">
      <DialogTitle id="new-study-title">{tr("Γρήγορη μελέτη", "Quick study")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }} id="new-study-kind-label">
          {tr("Τι μελέτη θέλετε;", "What study do you want?")}
        </Typography>
        <Stack spacing={1.25} role="radiogroup" aria-labelledby="new-study-kind-label">
          {options.map((option) => {
            const selected = option.value === kind;
            return (
              <Box key={option.value} role="radio" aria-checked={selected} tabIndex={0}
                data-testid={`new-study-${option.value}`}
                onClick={() => setKind(option.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setKind(option.value); } }}
                sx={{
                  border: 2, borderColor: selected ? "primary.main" : "divider", borderRadius: 2,
                  p: 1.5, cursor: "pointer", bgcolor: selected ? "action.selected" : "transparent",
                  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{option.title}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>{option.text}</Typography>
              </Box>
            );
          })}
        </Stack>
        <FormControlLabel sx={{ mt: 1 }}
          control={<Checkbox size="small" checked={batteryOn} onChange={(e) => setBatteryOn(e.target.checked)}
            inputProps={{ "data-testid": "new-study-with-battery" }} />}
          label={<Typography variant="body2">{tr("Με μπαταρία (επιλέγεται από τον κατάλογο, στην ενότητα «Σύστημα»)",
            "With a battery (chosen from the catalog, under \"System\")")}</Typography>} />

        <Box data-testid="new-study-fields">
          {section(tr("Στοιχεία πελάτη", "Customer details"))}
          <Stack spacing={1.5}>
            {/* Two fields per row, so the labels with their units are never cut off. */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 2 }} label={tr("Όνομα πελάτη / έργου", "Customer / project name")}
                value={name} onChange={(e) => setName(e.target.value)}
                inputProps={{ "data-testid": "new-study-name", maxLength: 200 }}
                helperText={tr("Το έργο αποθηκεύεται με αυτό το όνομα· κενό = «Χωρίς τίτλο».", "The project is saved under this name; blank = \"Untitled\".")} />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="new-study-consumer-label">{tr("Τύπος καταναλωτή", "Consumer type")}</InputLabel>
                <Select labelId="new-study-consumer-label" label={tr("Τύπος καταναλωτή", "Consumer type")}
                  value={useCase} onChange={(e) => setUseCase(e.target.value)}
                  inputProps={{ "data-testid": "new-study-consumer" }}>
                  <MenuItem value="Commercial">{tr("Επιχείρηση", "Business")}</MenuItem>
                  <MenuItem value="Residential Urban">{tr("Κατοικία", "Household")}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }}
                label={virtualNb ? tr("Κατανάλωση παροχών (kWh)", "Supplies' consumption (kWh)") : tr("Ετήσια κατανάλωση (kWh)", "Annual consumption (kWh)")}
                value={annualKwh} onChange={(e) => setAnnualKwh(e.target.value)} error={kwh.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-kwh" }}
                helperText={kwh.invalid ? tr("Δώστε θετικό αριθμό ή αφήστε κενό.", "Enter a positive number or leave blank.")
                  : virtualNb ? tr("Ετήσια· το άθροισμα όλων των παροχών.", "Annual; the sum of all the supplies.") : " "} />
              <TextField size="small" sx={{ flex: 1 }} label={tr("Τιμή ρεύματος (€/kWh)", "Electricity price (€/kWh)")}
                value={price} onChange={(e) => setPrice(e.target.value)} error={eurKwh.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-price" }}
                helperText={eurKwh.invalid
                  ? tr("Δώστε θετικό αριθμό ή αφήστε κενό.", "Enter a positive number or leave blank.")
                  : tr("Τελική τιμή, με χρεώσεις δικτύου και ΥΚΩ.", "Final price, with network charges and PSO.")} />
            </Stack>
            {business ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField size="small" sx={{ flex: 1 }} label={tr("Φορτίο βάσης (kW)", "Base load (kW)")}
                  value={baseLoad} onChange={(e) => setBaseLoad(e.target.value)} error={baseInvalid}
                  inputProps={{ inputMode: "decimal", "data-testid": "new-study-base-load" }}
                  helperText={baseKw.invalid ? tr("Δώστε 0 ή θετικό αριθμό, ή αφήστε κενό.", "Enter 0 or a positive number, or leave blank.")
                    : baseTooHigh ? tr(`Πρέπει να είναι κάτω από ${baseMaxKw.toFixed(2)} kW, τη μέση κατανάλωση.`,
                      `It must be below ${baseMaxKw.toFixed(2)} kW, the average consumption.`)
                      : baseSharePct != null ? tr(`Όλες τις ώρες: περίπου ${baseSharePct}% της ετήσιας κατανάλωσης.`,
                        `Every hour: about ${baseSharePct}% of the annual consumption.`)
                        : tr("Κατανάλωση και εκτός ωραρίου (ψυγεία, servers, συναγερμός)· κενό = καμία.",
                          "Consumption outside opening hours too (refrigeration, servers, alarm); blank = none.")} />
                <Box sx={{ flex: 1 }} />
              </Stack>
            ) : null}
          </Stack>

          {virtualNb ? (
            <>
              {section(tr("Εικονικός συμψηφισμός", "Virtual net billing"))}
              <Stack spacing={1.5} data-testid="new-study-vnb">
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField size="small" sx={{ flex: 1 }} label={tr("Τιμή ενέργειας (€/kWh)", "Energy price (€/kWh)")}
                    value={energyPrice} onChange={(e) => setEnergyPrice(e.target.value)} error={energyEurKwh.invalid || energyAbovePrice}
                    inputProps={{ inputMode: "decimal", "data-testid": "new-study-energy-price" }}
                    helperText={energyEurKwh.invalid ? tr("Δώστε θετικό αριθμό ή αφήστε κενό.", "Enter a positive number or leave blank.")
                      : energyAbovePrice ? tr("Δεν μπορεί να είναι πάνω από την τιμή ρεύματος.", "It cannot be above the electricity price.")
                        : tr("Το σκέλος ενέργειας του προμηθευτή, χωρίς ρυθμιζόμενες χρεώσεις.", "The supplier's energy part only, without regulated charges.")} />
                  <TextField size="small" sx={{ flex: 1 }} label={tr("Χρέωση ΛΠ1-3 (€/MWh)", "ΛΠ1-3 charge (€/MWh)")}
                    value={vnbCharge} onChange={(e) => setVnbCharge(e.target.value)} error={vnbChargeEur.invalid}
                    inputProps={{ inputMode: "decimal", "data-testid": "new-study-vnb-charge" }}
                    helperText={vnbChargeEur.invalid ? tr("Δώστε 0 ή θετικό αριθμό.", "Enter 0 or a positive number.")
                      : tr("Με τις απώλειες, επί της ενέργειας που συμψηφίζεται.", "Including losses, on the netted energy.")} />
                </Stack>
                <FormControlLabel sx={{ mt: -0.5 }}
                  control={<Checkbox size="small" checked={surplusPaid} onChange={(e) => setSurplusPaid(e.target.checked)}
                    inputProps={{ "data-testid": "new-study-surplus-paid" }} />}
                  label={<Typography variant="body2">{tr("Η περίσσεια πληρώνεται στην τιμή της αγοράς επόμενης ημέρας",
                    "The surplus is paid at the day-ahead price")}</Typography>} />
                <Typography variant="caption" component="p" sx={{ color: "text.secondary", mt: -0.5 }} data-testid="new-study-vnb-note">
                  {tr("Οι παροχές μελετώνται ως μία, με ποσοστό επιμερισμού 100%. Για ακριβή μελέτη, χωρίστε τις αργότερα στις Τεχνικές Παραδοχές, καθεμία με το ωριαίο αρχείο κατανάλωσής της, και υπολογίστε τα βέλτιστα ποσοστά.",
                    "The supplies are studied as one, with a 100% allocation. For an exact study, split them later on the Technical page, each with its hourly consumption file, and calculate the best percentages.")}
                </Typography>
              </Stack>
            </>
          ) : null}

          {section(tr("Σύστημα", "System"))}
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={tr("Ισχύς Φ/Β (kWp)", "PV size (kWp)")}
                value={pvKwp} onChange={(e) => setPvKwp(e.target.value)} error={kwpInvalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-kwp" }}
                helperText={kwpInvalid
                  ? tr("Δώστε θετικό αριθμό.", "Enter a positive number.")
                  : tr("Ο στόχος του αυτόματου σχεδιασμού.", "The auto-design target.")} />
              <FormControl size="small" sx={{ flex: 1.4 }}>
                <InputLabel id="new-study-mounting-label">{tr("Τοποθέτηση", "Mounting")}</InputLabel>
                <Select labelId="new-study-mounting-label" label={tr("Τοποθέτηση", "Mounting")}
                  value={mounting} onChange={(e) => setMounting(e.target.value)}
                  inputProps={{ "data-testid": "new-study-mounting" }}>
                  <MenuItem value="roof_frame">{tr("Επίπεδη στέγη / δώμα, σε βάσεις", "Flat roof, on frames")}</MenuItem>
                  <MenuItem value="roof_flush">{tr("Κεκλιμένη στέγη, πάνω στα κεραμίδια", "Pitched roof, on the tiles")}</MenuItem>
                  <MenuItem value="ground">{tr("Έδαφος", "Ground")}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={flush ? tr("Κλίση στέγης (°)", "Roof pitch (°)") : tr("Κλίση πάνελ (°)", "Panel tilt (°)")}
                value={tilt} onChange={(e) => setTilt(e.target.value)} error={tiltInvalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-tilt" }}
                helperText={tiltInvalid ? tr("Από 0 έως κάτω από 90.", "From 0 to below 90.") : " "} />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="new-study-facing-label">{flush ? tr("Προσανατολισμός στέγης", "Roof facing") : tr("Προσανατολισμός", "Facing")}</InputLabel>
                <Select labelId="new-study-facing-label" label={flush ? tr("Προσανατολισμός στέγης", "Roof facing") : tr("Προσανατολισμός", "Facing")}
                  value={azimuth} onChange={(e) => setAzimuth(Number(e.target.value))}
                  inputProps={{ "data-testid": "new-study-facing" }}>
                  {FACINGS.map((a) => <MenuItem key={a} value={a}>{facingLabel(a, isGreek)}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Autocomplete size="small" sx={{ flex: 1 }} disableClearable
                options={[...new Set([STUDY_PRESET_MODULE, module, ...(catalog.modules || []).map(itemName).filter(Boolean)])]}
                loading={catalogLoading} value={module}
                onChange={(_, value) => setModule(value || STUDY_PRESET_MODULE)}
                renderOption={(props, option) => <li {...props} key={option}><span data-no-translate>{option}</span></li>}
                renderInput={(params) => (
                  <TextField {...params} label={tr("Πάνελ", "Module")}
                    inputProps={{ ...params.inputProps, "data-testid": "new-study-module" }} />
                )} />
              <Autocomplete size="small" sx={{ flex: 1 }}
                options={catalog.inverters || []} loading={catalogLoading}
                value={inverter} onChange={(_, value) => setInverter(value)}
                getOptionLabel={itemName} isOptionEqualToValue={(a, b) => itemName(a) === itemName(b)}
                renderOption={(props, item) => <li {...props} key={itemName(item)}><span data-no-translate>{itemName(item)}</span></li>}
                renderInput={(params) => (
                  <TextField {...params} label={tr("Αντιστροφέας", "Inverter")}
                    placeholder={tr("Αυτόματα", "Automatic")}
                    inputProps={{ ...params.inputProps, "data-testid": "new-study-inverter" }}
                    helperText={tr("Κενό: τον επιλέγει ο αυτόματος σχεδιασμός.", "Blank: auto-design chooses it.")} />
                )} />
            </Stack>
            {withBattery ? (
              <Autocomplete size="small" fullWidth
                options={catalog.batteries || []} loading={open && !catalog.batteries}
                value={battery} onChange={(_, value) => setBattery(value)}
                getOptionLabel={itemName} isOptionEqualToValue={(a, b) => itemName(a) === itemName(b)}
                renderOption={(props, b) => <li {...props} key={itemName(b)}><span data-no-translate>{itemName(b)}</span></li>}
                renderInput={(params) => (
                  <TextField {...params} label={tr("Μπαταρία", "Battery")}
                    inputProps={{ ...params.inputProps, "data-testid": "new-study-battery" }}
                    helperText={catalog.batteryError
                      ? tr("Δεν φορτώθηκε ο κατάλογος μπαταριών. Μπορείτε να επιλέξετε μπαταρία αργότερα, στη Ρύθμιση Μονάδας → Αποθήκευση.", "The battery catalog could not be loaded. You can choose a battery later, under Unit Setup → Storage.")
                      : tr("Από τον κατάλογο.", "From the catalog.")} />
                )} />
            ) : null}
            {catalog.error ? <Alert severity="info">{catalog.error}</Alert> : null}
          </Stack>

          {section(tr("Τιμές (χωρίς ΦΠΑ) και οικονομικά", "Prices (before VAT) and economics"))}
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={tr("Φ/Β (€/kWp)", "PV (€/kWp)")}
                value={pvPrice} onChange={(e) => setPvPrice(e.target.value)} error={pvEur.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-pv-price" }}
                helperText={pvEur.invalid ? tr("Δώστε θετικό αριθμό.", "Enter a positive number.") : " "} />
              <TextField size="small" sx={{ flex: 1 }} label={tr("Συντήρηση Φ/Β (%/έτος)", "PV O&M (%/year)")}
                value={pvOm} onChange={(e) => setPvOm(e.target.value)} error={pvOmPct.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-pv-om" }}
                helperText={pvOmPct.invalid ? tr("Δώστε 0 ή θετικό αριθμό.", "Enter 0 or a positive number.") : " "} />
            </Stack>
            {withBattery ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField size="small" sx={{ flex: 1 }} label={tr("Μπαταρία (€/kWh)", "Battery (€/kWh)")}
                  value={batteryPrice} onChange={(e) => setBatteryPrice(e.target.value)} error={batteryEur.invalid}
                  inputProps={{ inputMode: "decimal", "data-testid": "new-study-battery-price" }}
                  helperText={batteryEur.invalid ? tr("Δώστε θετικό αριθμό.", "Enter a positive number.") : " "} />
                <TextField size="small" sx={{ flex: 1 }} label={tr("Συντήρηση μπαταρίας (%/έτος)", "Battery O&M (%/year)")}
                  value={batteryOm} onChange={(e) => setBatteryOm(e.target.value)} error={batteryOmPct.invalid}
                  inputProps={{ inputMode: "decimal", "data-testid": "new-study-battery-om" }}
                  helperText={batteryOmPct.invalid ? tr("Δώστε 0 ή θετικό αριθμό.", "Enter 0 or a positive number.") : " "} />
              </Stack>
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={tr("Λοιπά έργα (€)", "Other works (€)")}
                value={otherWorks} onChange={(e) => setOtherWorks(e.target.value)} error={otherWorksEur.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-other-works" }}
                helperText={otherWorksEur.invalid ? tr("Δώστε 0 ή θετικό αριθμό, ή αφήστε κενό.", "Enter 0 or a positive number, or leave blank.")
                  : tr("Ένα ποσό για όλο το έργο· κενό = κανένα.", "One amount for the whole project; blank = none.")} />
              <TextField size="small" sx={{ flex: 1 }} label={tr("Ασφάλιστρο (%/έτος)", "Insurance (%/year)")}
                value={insurance} onChange={(e) => setInsurance(e.target.value)} error={insurancePct.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-insurance" }}
                helperText={insurancePct.invalid ? tr("Από 0 έως κάτω από 100.", "From 0 to below 100.")
                  : tr("Ως ποσοστό της επένδυσης.", "As a share of the investment.")} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={tr("Πληθωρισμός (%/έτος)", "Inflation (%/year)")}
                value={inflation} onChange={(e) => setInflation(e.target.value)} error={inflationPct.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-inflation" }}
                helperText={inflationPct.invalid ? tr("Από 0 έως κάτω από 100.", "From 0 to below 100.") : " "} />
              <TextField size="small" sx={{ flex: 1 }} label={tr("Προεξόφληση (%/έτος)", "Discount rate (%/year)")}
                value={nominal} onChange={(e) => setNominal(e.target.value)} error={nominalPct.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-discount" }}
                helperText={nominalPct.invalid ? tr("Από 0 έως κάτω από 100.", "From 0 to below 100.")
                  : tr("Ονομαστικό προεξοφλητικό επιτόκιο.", "Nominal discount rate.")} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={tr("Μίσθωση γης (€/έτος)", "Land lease (€/year)")}
                value={landLease} onChange={(e) => setLandLease(e.target.value)} error={landLeaseEur.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-land-lease" }}
                helperText={landLeaseEur.invalid ? tr("Δώστε 0 ή θετικό αριθμό, ή αφήστε κενό.", "Enter 0 or a positive number, or leave blank.")
                  : tr("Κενό = καμία (π.χ. ιδιόκτητη στέγη).", "Blank = none (e.g. an owned roof).")} />
              <Box sx={{ flex: 1 }} />
            </Stack>
            <Typography variant="caption" component="p" sx={{ color: "text.secondary", mt: -0.5 }} data-testid="new-study-prices-note">
              {ownDefaults
                ? tr("Οι τιμές, τα οικονομικά και οι επιλογές του συστήματος είναι οι δικές σας προεπιλογές, όπως τις αποθηκεύσατε σε αυτόν τον browser. Αλλάζουν και αργότερα, στις Οικονομικές Παραδοχές.",
                  "The prices, economics and system choices are your own defaults, as saved in this browser. They can also be changed later, under Economic assumptions.")
                : tr(`Οι αρχικές τιμές (${PRESET_PRICES.pvEurPerKwp} €/kWp${withBattery ? `, ${PRESET_PRICES.batteryEurPerKwh} €/kWh` : ""}) είναι ενδεικτικές· βάλτε τις δικές σας. Αλλάζουν και αργότερα, στις Οικονομικές Παραδοχές.`,
                  `The starting prices (${PRESET_PRICES.pvEurPerKwp} €/kWp${withBattery ? `, ${PRESET_PRICES.batteryEurPerKwh} €/kWh` : ""}) are indicative; enter your own. They can also be changed later, under Economic assumptions.`)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1 }}>
              <FormControlLabel sx={{ mr: 0 }}
                control={<Checkbox size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  inputProps={{ "data-testid": "new-study-remember" }} />}
                label={<Typography variant="body2">{tr(
                  "Αποθήκευση ως προεπιλογή για τα νέα μου έργα.",
                  "Save as the default for my new projects.")}</Typography>} />
              {ownDefaults ? (
                <Button size="small" onClick={resetStartingValues} data-testid="new-study-reset-defaults">
                  {tr("Επαναφορά στις αρχικές τιμές της πλατφόρμας", "Back to the platform's starting values")}
                </Button>
              ) : null}
            </Box>
          </Stack>
        </Box>

        <Typography variant="caption" component="p" sx={{ color: "text.secondary", mt: 1.5 }} data-testid="new-study-defaults">
          {tr(`Αρχικές ρυθμίσεις: Χαμηλή Τάση 400 V, σκίαση ${PRESET_SHADING_PCT}% ως ετήσιο ποσοστό, ετήσια κατανάλωση, μία συνολική τιμή ανά kWh, απλοποιημένη κοστολόγηση και οικονομικά όπως παραπάνω, 20 έτη, χωρίς δάνειο${kind !== "zero_feed_in" ? ", μελλοντική τιμή της ενέργειας που εγχέεται από την προθεσμιακή καμπύλη τιμών της MetEn" : ""}. Όλα αλλάζουν στη συνέχεια.`,
            `Starting settings: low voltage 400 V, shading ${PRESET_SHADING_PCT}% as an annual percentage, annual consumption, one total price per kWh, simplified costing and economics as above, 20 years, no loan${kind !== "zero_feed_in" ? ", future price of exported energy from the MetEn forward price curve" : ""}. Everything can be changed afterwards.`)}
          <br />
          {tr(`Το έργο αποθηκεύεται με τη δημιουργία. Επόμενα βήματα: ${steps.join(" → ")}.`,
            `The project is saved when it is created. Next steps: ${steps.join(" → ")}.`)}
          <br />
          {runMissing.length
            ? tr(`Για «Δημιουργία και εκτέλεση» χρειάζονται: ${runMissing.join(", ")}.`,
              `"Create and run" needs: ${runMissing.join(", ")}.`)
            : tr("Με «Δημιουργία και εκτέλεση» ορίζετε μόνο τη θέση στον χάρτη· τα υπόλοιπα γίνονται αυτόματα.",
              "With \"Create and run\" you only set the location on the map; the rest is done for you.")}
        </Typography>
        {error ? <Alert severity="error" sx={{ mt: 1.5 }} data-testid="new-study-error">{error}</Alert> : null}
      </DialogContent>
      {openProjectName ? (
        <Typography variant="caption" component="p" data-testid="new-study-open-project"
          sx={{ px: 3, pt: 1.5, color: "text.secondary" }}>
          {tr(`Το ανοιχτό έργο «${openProjectName}» θα κλείσει. Με «Άκυρο» παραμένει ανοιχτό.`,
            `The open project "${openProjectName}" will close. Cancel keeps it open.`)}
        </Typography>
      ) : null}
      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onSkip} disabled={busy} data-testid="new-study-skip" sx={{ mr: "auto" }}>
          {tr("Παράλειψη (βασικές ρυθμίσεις)", "Skip (basic settings)")}
        </Button>
        <Button onClick={onCancel} disabled={busy}>{tr("Άκυρο", "Cancel")}</Button>
        <Button variant="outlined" disabled={invalid || busy} data-testid="new-study-create" onClick={() => create(false)}>
          {tr("Δημιουργία έργου", "Create project")}
        </Button>
        <Button variant="contained" disabled={invalid || busy || runMissing.length > 0} data-testid="new-study-create-run"
          onClick={() => create(true)}
          startIcon={busy ? <CircularProgress color="inherit" size={16} /> : undefined}>
          {tr("Δημιουργία και εκτέλεση", "Create and run")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
